import { useEffect, useState } from "react";
import { fetchMentorRequests } from "../lib/api";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "./ui/tabs";
import MentorRequestRow from "./MentorRequestRow";
import MentorInboxPanel from "./MentorInboxPanel";
import { Inbox, Users, Clock, CheckCircle2, XCircle, GraduationCap, Send } from "lucide-react";

/**
 * Manager Mentor Panel — yönetici hem kendi gelen kutusunu (kendi mentor olarak)
 * hem de takım üyelerinin mentor aktivitesini görür.
 */
export default function ManagerMentorPanel({ managerId, teamMembers = [] }) {
  const [view, setView] = useState("personal");
  const [teamItems, setTeamItems] = useState([]);
  const [teamFilter, setTeamFilter] = useState("pending");
  const [teamLoading, setTeamLoading] = useState(true);

  const teamIds = new Set(teamMembers.map((t) => t.id));

  const reloadTeam = async () => {
    if (teamIds.size === 0) return;
    setTeamLoading(true);
    try {
      // Fetch all requests where team member is involved (either side)
      const all = await fetchMentorRequests({});
      const filtered = (all.items || []).filter(
        (it) => teamIds.has(it.mentee_id) || teamIds.has(it.mentor_id),
      );
      setTeamItems(filtered);
    } catch {
      // silent
    } finally {
      setTeamLoading(false);
    }
  };

  useEffect(() => {
    if (view === "team") reloadTeam();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [view, managerId, teamMembers.length]);

  // Compute team counts
  const teamCounts = teamItems.reduce(
    (acc, it) => {
      acc[it.status] = (acc[it.status] || 0) + 1;
      acc.total += 1;
      return acc;
    },
    { pending: 0, approved: 0, rejected: 0, completed: 0, total: 0 },
  );

  const filteredItems = teamFilter === "all" ? teamItems : teamItems.filter((i) => i.status === teamFilter);

  return (
    <div className="space-y-5" data-testid="manager-mentor-panel">
      {/* Top toggle: Personal inbox vs Team activity */}
      <Tabs value={view} onValueChange={setView}>
        <TabsList className="bg-slate-100 border border-slate-200 mb-5 p-1 h-auto">
          <TabsTrigger
            value="personal"
            data-testid="mgr-mentor-view-personal"
            className="text-sm px-4 py-1.5 data-[state=active]:bg-white data-[state=active]:text-sky-800 data-[state=active]:shadow-sm"
          >
            <Inbox className="w-3.5 h-3.5 mr-1.5" /> Bana Gelen Talepler
          </TabsTrigger>
          <TabsTrigger
            value="team"
            data-testid="mgr-mentor-view-team"
            className="text-sm px-4 py-1.5 data-[state=active]:bg-white data-[state=active]:text-violet-800 data-[state=active]:shadow-sm"
          >
            <Users className="w-3.5 h-3.5 mr-1.5" /> Takım Aktivitesi
          </TabsTrigger>
        </TabsList>

        <TabsContent value="personal" className="space-y-5">
          <MentorInboxPanel employeeId={managerId} />
        </TabsContent>

        <TabsContent value="team" className="space-y-5">
          {/* Team hero */}
          <div className="card-surface p-6 bg-gradient-to-br from-violet-50 via-white to-white border-violet-200">
            <div className="flex items-start gap-4">
              <div className="w-11 h-11 rounded-xl bg-violet-500/15 border border-violet-300 flex items-center justify-center flex-shrink-0">
                <Users className="w-5 h-5 text-violet-700" />
              </div>
              <div className="flex-1">
                <div className="label-eyebrow text-violet-700 mb-1">Takım Mentor Aktivitesi</div>
                <h3 className="font-display text-2xl font-semibold tracking-tight">
                  {teamCounts.pending > 0
                    ? <><span className="text-violet-700">{teamCounts.pending}</span> takım üyesi onay bekliyor</>
                    : teamCounts.total > 0
                      ? "Takımının mentor aktivitesini takip et"
                      : "Takımında henüz mentor talebi yok"}
                </h3>
                <p className="text-sm text-slate-600 mt-1.5">
                  Takım üyelerinin gönderdiği veya aldığı tüm mentor talepleri burada — yönetici olarak
                  görünürlüğün artar.
                </p>
              </div>
            </div>
            <div className="mt-5 grid grid-cols-2 md:grid-cols-5 gap-3">
              <StatBox label="Toplam" value={teamCounts.total} accent="slate" Icon={Send} />
              <StatBox label="Bekleyen" value={teamCounts.pending} accent="amber" Icon={Clock} />
              <StatBox label="Onaylı" value={teamCounts.approved} accent="emerald" Icon={CheckCircle2} />
              <StatBox label="Reddedilen" value={teamCounts.rejected} accent="rose" Icon={XCircle} />
              <StatBox label="Tamamlanan" value={teamCounts.completed} accent="sky" Icon={GraduationCap} />
            </div>
          </div>

          <Tabs value={teamFilter} onValueChange={setTeamFilter}>
            <TabsList className="bg-white border border-slate-200 mb-4 p-1.5 h-auto flex-wrap">
              <TabsTrigger value="pending" className="text-sm px-4 py-1.5">Bekleyen ({teamCounts.pending})</TabsTrigger>
              <TabsTrigger value="approved" className="text-sm px-4 py-1.5">Onaylı ({teamCounts.approved})</TabsTrigger>
              <TabsTrigger value="rejected" className="text-sm px-4 py-1.5">Reddedilen ({teamCounts.rejected})</TabsTrigger>
              <TabsTrigger value="completed" className="text-sm px-4 py-1.5">Tamamlanan ({teamCounts.completed})</TabsTrigger>
              <TabsTrigger value="all" className="text-sm px-4 py-1.5">Tümü ({teamCounts.total})</TabsTrigger>
            </TabsList>
            <TabsContent value={teamFilter} className="space-y-3">
              {teamLoading && (
                <div className="card-surface p-8">
                  <div className="h-4 bg-slate-200/60 rounded animate-pulse w-1/3 mb-2" />
                  <div className="h-3 bg-slate-100 rounded animate-pulse w-2/3" />
                </div>
              )}
              {!teamLoading && filteredItems.length === 0 && (
                <div className="card-surface p-12 text-center">
                  <Inbox className="w-10 h-10 mx-auto text-slate-300 mb-3" />
                  <h3 className="font-display text-lg font-semibold text-slate-900">Bu kategoride talep yok</h3>
                  <p className="text-sm text-slate-500 mt-1.5">
                    Takım üyelerin mentor aktivitesine başladığında burada görünecek.
                  </p>
                </div>
              )}
              {!teamLoading && filteredItems.map((it) => (
                <MentorRequestRow key={it.id} item={it} onUpdate={reloadTeam} perspective="hr" />
              ))}
            </TabsContent>
          </Tabs>
        </TabsContent>
      </Tabs>
    </div>
  );
}

function StatBox({ label, value, accent, Icon }) {
  const colors = {
    slate: "border-slate-200 text-slate-800 bg-slate-50",
    amber: "border-amber-300 text-amber-800 bg-amber-50",
    emerald: "border-emerald-300 text-emerald-800 bg-emerald-50",
    rose: "border-rose-300 text-rose-800 bg-rose-50",
    sky: "border-sky-300 text-sky-800 bg-sky-50",
  }[accent];
  return (
    <div className={`rounded-lg border ${colors} px-3 py-2.5`}>
      <div className="flex items-center justify-between mb-1">
        <span className="text-[0.6rem] uppercase tracking-wider opacity-80">{label}</span>
        <Icon className="w-3.5 h-3.5 opacity-70" />
      </div>
      <div className="font-mono text-xl font-semibold">{value}</div>
    </div>
  );
}
